import type { SubmitTokenUnion, SubmitTokenWithAgreementData } from '@/components/escript/token/types';
import { GoogleAnalyticsEscriptEventName } from '@/hooks/analytics/types';
import useGoogleAnalytics from '@/hooks/analytics/useGoogleAnalytics';
import type { TBeginEscriptSignupResponse } from '@/hooks/escript/useBeginEscriptSignup';
import { useBeginEscriptSignup } from '@/hooks/escript/useBeginEscriptSignup';
import { usePreOnboardingContext } from '@/hooks/escript/usePreOnboardingContext';
import { SubmitTokenTextWrapper } from '@/pages/escript/landing/SubmitSectionStyles';
import { PRODUCT_PURCHASE_ISSUE_TEXT } from '@/pages/escript/order-medication/util';
import { isContactDetailsResponse, isResendInviteResponse } from '@/utils/beginEscriptSignupUtils';
import { emitUIEvent } from '@montugroup/data-collection';
import { Stack, Typography } from '@montugroup/design-system';
import type { SubmitErrorHandler } from 'react-hook-form';
import { FormProvider, useFormContext, useFormState } from 'react-hook-form';
import { TokenInputWithAgreement } from '../../token/TokenInputWithAgreement';
import { TokenLoading } from './TokenLoading';

export interface SubmitTokenProps {
  setOpenTokenDialog: (state: boolean) => void;
  openTokenDialog: boolean;
}

const emitSuccessEvent = (message: string) => {
  emitUIEvent(
    {
      component_name: 'submit_token',
      event_name: 'success',
      id: 'escript_onboarding_submit_token_success',
      value: `Submit token success - ${message}`,
    },
    [],
  );
};

const emitErrorEvent = (message: string) => {
  emitUIEvent(
    {
      component_name: 'submit_token',
      event_name: 'error',
      id: 'escript_onboarding_submit_token_error',
      value: `Submit token error - ${message}`,
    },
    [],
  );
};

export const SubmitToken = ({ setOpenTokenDialog, openTokenDialog }: SubmitTokenProps) => {
  const { setPreOnboardingState, setCustomerDetails } = usePreOnboardingContext();
  const form = useFormContext<SubmitTokenWithAgreementData>();
  const { setError } = form;
  const { beginEscriptSignupMutate, isPending: beginEscriptSignupIsPending } = useBeginEscriptSignup();
  const { errors } = useFormState<SubmitTokenWithAgreementData>();
  const { sendGoogleAnalyticsEvent } = useGoogleAnalytics();

  const handleProcessTokenSuccess = (data: TBeginEscriptSignupResponse) => {
    // GA event for submit attempts
    sendGoogleAnalyticsEvent(GoogleAnalyticsEscriptEventName.PRESCRIPTION_SUBMIT_ATTEMPT, {});

    if (isResendInviteResponse(data)) {
      setCustomerDetails({ preOnboardUserId: data.preOnboardUserId! });
    }
    if (isContactDetailsResponse(data)) {
      setCustomerDetails({ email: data.email, phone: data.phone, preOnboardUserId: data.preOnboardUserId });
    }

    switch (data.onboardState) {
      case 'ACCOUNT_ALREADY_EXISTS':
        emitErrorEvent('Customer is already a registered escript customer');
        setPreOnboardingState('ACCOUNT_ALREADY_EXISTS');
        break;
      case 'DUPLICATE_TOKEN_PENDING_INVITE':
        emitErrorEvent('The script is a duplicate but the customer is not registered');
        setPreOnboardingState('DUPLICATE_TOKEN_PENDING_INVITE');
        break;
      case 'INVALID_TOKEN_PENDING_INVITE':
        emitErrorEvent('The script is ineligible however the customer has previously been invited');
        setPreOnboardingState('INVALID_TOKEN_PENDING_INVITE');
        break;
      case 'SCRIPT_AWAITING_REVIEW':
        emitSuccessEvent('script awaiting review');
        setPreOnboardingState('SCRIPT_AWAITING_REVIEW');
        break;
      case 'SCRIPT_VALID':
        emitSuccessEvent('script valid');
        setPreOnboardingState('SCRIPT_VALID');
        break;
      case 'SCRIPT_VALID_OUT_OF_STOCK':
        emitSuccessEvent('script valid out of stock');
        setPreOnboardingState('SCRIPT_VALID_OUT_OF_STOCK');
        break;
      case 'ERX_FALLBACK_STRATEGY':
        emitSuccessEvent('erx fallback strategy');
        setPreOnboardingState('ERX_FALLBACK_STRATEGY');
        break;
      case 'SCRIPT_UNSUPPORTED':
        setError('tokenInput', {
          type: 'custom',
          message: PRODUCT_PURCHASE_ISSUE_TEXT.scriptUnavailable.info[0],
        });
        emitUIEvent(
          {
            component_name: 'submit_token',
            event_name: 'error',
            id: 'escript_onboarding_submit_token_error',
            value: `INELIGIBLE ${PRODUCT_PURCHASE_ISSUE_TEXT.scriptUnavailable.info[0]}`,
          },
          [],
        );
        // GA event for submit errors
        sendGoogleAnalyticsEvent(GoogleAnalyticsEscriptEventName.PRESCRIPTION_SUBMIT_ERROR, {
          error_code: 'script_unsupported',
        });
        break;
    }
  };

  const handleProcessTokenError = () => {
    const errorMessage = 'Unfortunately, we cannot find a valid prescription under this token';
    setError('tokenInput', {
      type: 'custom',
      message: errorMessage,
    });

    // GA event for submit attempts
    sendGoogleAnalyticsEvent(GoogleAnalyticsEscriptEventName.PRESCRIPTION_SUBMIT_ATTEMPT, {});

    // snowplow event to track failures
    emitUIEvent(
      {
        component_name: 'submit_token',
        event_name: 'error',
        id: 'escript_onboarding_submit_token_error',
        value: errorMessage,
      },
      [],
    );

    // GA event for submit errors
    sendGoogleAnalyticsEvent(GoogleAnalyticsEscriptEventName.PRESCRIPTION_SUBMIT_ERROR, {});
  };

  const onTokenInvalid: SubmitErrorHandler<SubmitTokenUnion> = () => {
    if (errors.tokenInput) {
      // GA event for submit attempts
      sendGoogleAnalyticsEvent(GoogleAnalyticsEscriptEventName.PRESCRIPTION_SUBMIT_ERROR, {
        error_code: 'invalid_token',
      });
      // snowplow event to track validation failures
      emitUIEvent(
        {
          component_name: 'submit_token',
          event_name: 'error',
          id: 'escript_onboarding_submit_token_error',
          value: errors.tokenInput.message,
        },
        [],
      );

      // GA event for submit errors
      sendGoogleAnalyticsEvent(GoogleAnalyticsEscriptEventName.PRESCRIPTION_SUBMIT_ERROR, {});
    }
  };

  const handleTokenSubmit = (formData: SubmitTokenUnion) => {
    beginEscriptSignupMutate(formData.tokenInput, handleProcessTokenSuccess, handleProcessTokenError);
  };

  if (beginEscriptSignupIsPending) {
    return <TokenLoading />;
  }

  return (
    <FormProvider {...form}>
      <Stack spacing={{ xs: 8, md: 10 }}>
        <SubmitTokenTextWrapper>
          <Typography variant="subtitle2" fontWeight={600}>
            Enter your script token
          </Typography>
          <Typography>
            Submit your prescription and we’ll take care of the rest - quickly, discreetly, and with care at every step.
          </Typography>
        </SubmitTokenTextWrapper>
        <TokenInputWithAgreement
          setOpenTokenDialog={setOpenTokenDialog}
          openTokenDialog={openTokenDialog}
          handleTokenSubmit={handleTokenSubmit}
          onTokenInvalid={onTokenInvalid}
        />
      </Stack>
    </FormProvider>
  );
};
